"use client";
import { createContext, useCallback, useEffect, useState } from "react";
import { Noto_Kufi_Arabic, Poppins } from "next/font/google";
import { normalizeLang, readStoredLang, syncLangInUrl, type Rv2Lang } from "@/lib/rewards-v2/i18n";

const fontFr = Poppins({
  weight: ["100", "200", "300", "400", "500", "600", "700"],
  subsets: ["latin"],
});

const fontAr = Noto_Kufi_Arabic({
  weight: ["100", "200", "300", "400", "500", "600", "700"],
  subsets: ["arabic"],
});

type langType = {
  lang: string;
  setLang: (lang: string) => void;
};

export const languageContext = createContext<langType | null>(null);

function resolveInitialLang(): Rv2Lang {
  if (typeof window === "undefined") return "fr";
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get("lang");
  if (urlLang) return normalizeLang(urlLang);
  return readStoredLang() ?? "fr";
}

export const LanguageProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [lang, setLangState] = useState("fr");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const initial = resolveInitialLang();
    setLangState(initial);
    setReady(true);
  }, []);

  const setLang = useCallback((next: string) => {
    const normalized = normalizeLang(next);
    setLangState(normalized);
    try {
      window.localStorage.setItem("lang", normalized);
    } catch {
      /* ignore */
    }
    syncLangInUrl(normalized);
  }, []);

  useEffect(() => {
    if (!ready) return;
    try {
      window.localStorage.setItem("lang", lang);
    } catch {
      /* ignore */
    }
    const root = document.documentElement;
    const onRewardsV2 = typeof window !== "undefined" && window.location.pathname.startsWith("/rewards_v2");
    if (lang === "fr" || lang === "en") {
      if (!onRewardsV2) root.setAttribute("class", fontFr.className);
      else root.removeAttribute("class");
      root.setAttribute("dir", "ltr");
      root.setAttribute("lang", lang);
    } else {
      root.setAttribute("class", fontAr.className);
      root.setAttribute("dir", "rtl");
      root.setAttribute("lang", lang);
    }
  }, [lang, ready]);

  return (
    <languageContext.Provider value={{ lang, setLang }}>
      {children}
    </languageContext.Provider>
  );
};
